// File: js/db/ledger-write.js
//
// The money write path (spec §7, invariants 2/3/4/5).
//
// Every real-world sale gets its UUID exactly ONCE — here, at the moment the
// cashier confirms, BEFORE any network attempt — and it never changes. Retries
// replay the same id, the server's `on conflict (id) do nothing` swallows the
// duplicate, and the ack comes back `already_present`, which is SUCCESS. That is
// the whole of the idempotency story, and it is why audit H4 (commit succeeded,
// response lost) stops costing money.
//
// The write is ONE Dexie transaction over three tables:
//   1. the ledger mirror row   (`synced: 0`)
//   2. the outbox row          (the §6 wire payload, same UUID)
//   3. receipt-number consumption (`meta.block.next++`)
// `add`, never `put`: a key clash must roll the WHOLE transaction back, taking the
// receipt number with it, rather than silently overwrite a row that already holds
// a patient's money.
//
// The write path carries NO mutable-state precondition (invariant 4). It does not
// read a stock level, a daily total, or a balance, so a stale precondition can
// never bounce or corrupt a sync. Stock is a fold (spec §4); the client's
// sell-beyond-known-stock gate is a UI-layer courtesy, not a condition of the
// write, because an offline oversell is a fact about the world, not an error.

import { getMeta, setMeta, META_KEYS } from './schema.js';
import { isPersistGranted } from './persistence.js';
import { readBlockState, takeReceiptNumber } from './receipt-blocks.js';
import { nextQueuedAt } from './queue-clock.js';
import { ValidationError, CollectionRefusedError } from './errors.js';

/**
 * The §6 wire contract, frozen, in order. `business_date` is NOT among them — it is
 * server-derived (invariant 9), and a client copy on the wire would be a second
 * authority for the same fact. We keep a local copy on the MIRROR row only, so the
 * device can fold its own day while offline.
 */
const WIRE_FIELDS = [
    'id', 'kind', 'channel', 'amount', 'items', 'patient_name',
    'reverses_id', 'reason', 'receipt_no', 'book_no',
    'device_id', 'device_time', 'collected_by'
];

const CHANNELS = new Set(['regie', 'pharmacy']);
const ITEM_KINDS = new Set(['revenue_type', 'drug']);

/**
 * Whether this device may accept cash right now. **This function is the gate.**
 * The UI renders its verdict; it does not add conditions of its own — a rule
 * enforced in the UI layer is a rule any other caller walks straight past.
 *
 * The order is load-bearing and is spelled out in spec §7: **durability, then
 * identity, then receipt supply.**
 *
 * 1. `storage-not-persistent` — first because it is the most dangerous condition.
 *    A device that cannot durably hold cash must be told that before anything
 *    else; telling that cashier "you're out of receipt numbers" sends them to
 *    fetch a new block while the browser is free to evict the money already taken.
 * 2. `device-not-provisioned` — a device with no `meta.device_id` queues sales
 *    whose server-side conflict row carries a null `device_id`: a parked event
 *    that appears in NO device's queue and rots there. That is invariant 6
 *    violated by omission — the event failed, a human was supposed to see it, and
 *    nobody can. (Added to §7 on 27 July 2026, found during this build.)
 * 3. `no-receipt-numbers` — last, because it is the one a cashier can work around
 *    on paper without anything being lost.
 *
 * @param {import('dexie').Dexie} db
 * @returns {Promise<{allowed:boolean, reason?:CollectionRefusalReason}>}
 */
export async function collectionAllowed(db) {
    if (!(await isPersistGranted(db))) {
        return { allowed: false, reason: 'storage-not-persistent' };
    }
    if (!isPresent(await getMeta(db, META_KEYS.DEVICE_ID, null))) {
        return { allowed: false, reason: 'device-not-provisioned' };
    }
    const block = await readBlockState(db);
    if (block.exhausted) {
        return { allowed: false, reason: 'no-receipt-numbers' };
    }
    return { allowed: true };
}

/**
 * @typedef {'storage-not-persistent'|'device-not-provisioned'|'no-receipt-numbers'} CollectionRefusalReason
 */

/**
 * Record a SALE: mints the UUID once, consumes one receipt number, and writes the
 * mirror + outbox rows in a single atomic transaction. Refuses — with a typed
 * error, never a green banner — before touching storage when collection is not
 * allowed.
 *
 * @param {import('dexie').Dexie} db
 * @param {{
 *   channel:'regie'|'pharmacy',
 *   amount:number,
 *   items:{item_id:string, item_kind:'revenue_type'|'drug', name?:string,
 *          qty:number, unit_price:number, line_total:number}[],
 *   device_id:string,
 *   device_time:string,
 *   business_date:string,
 *   patient_name?:string|null,
 *   book_no?:string|null,
 *   collected_by?:string|null
 * }} fields Caller-supplied; `business_date` comes from `money/business-date.js`.
 * @param {{uuid?: () => string, queuedAt?: number}} [options] Injectable UUID and
 *   queue position, for tests. Production supplies neither.
 * @returns {Promise<{id:string, receipt_no:number, queued_at:number}>}
 * @throws {ValidationError|CollectionRefusedError}
 */
export async function recordSale(db, fields, options = {}) {
    const { uuid = defaultUuid } = options;

    // The DEVICE-level gate runs first, before the EVENT-level validation. "May
    // this device collect at all?" precedes "is this sale well-formed?", and the
    // ordering is what makes the refusal actionable: an unprovisioned device must
    // hear « Cet appareil n'est pas encore enregistré… », not a generic complaint
    // about a missing `device_id` field, which tells a cashier nothing they can do
    // anything about. It also means a refused sale leaves no trace anywhere —
    // nothing is minted, nothing is written, no receipt number is burned.
    const gate = await collectionAllowed(db);
    if (!gate.allowed) throw new CollectionRefusedError(/** @type {any} */(gate.reason));

    // `device_id` defaults to this device's registered identity. The gate above
    // has already established that one exists, so this can only fill in a value
    // the caller left off — it never invents one.
    const src = (fields && typeof fields === 'object') ? { ...fields } : fields;
    if (src && typeof src === 'object' && !isPresent(src.device_id)) {
        src.device_id = await getMeta(db, META_KEYS.DEVICE_ID, null);
    }

    const issues = [];
    validateSale(src, issues);
    if (issues.length > 0) throw new ValidationError(issues);

    // Invariant 2: minted exactly once, here, before any network attempt.
    const id = uuid();
    // Spec §7: `max(Date.now(), last+1)`. Taken AFTER the gate so a refused sale
    // does not burn a queue position.
    const queued_at = options.queuedAt === undefined ? nextQueuedAt() : options.queuedAt;

    /** @type {{id:string, receipt_no:number, queued_at:number}} */
    let result;

    await db.transaction('rw', db.table('ledger'), db.table('outbox'), db.table('meta'), async () => {
        // Consumed INSIDE the transaction: spent exactly when used, rolled back with
        // everything else on any failure, and re-checked here against the TOCTOU
        // window the gate above cannot close.
        const receipt_no = await takeReceiptNumber(db);

        const wire = buildWire({
            id,
            kind: 'sale',
            channel: src.channel,
            amount: src.amount,
            items: src.items,
            patient_name: src.patient_name ?? null,
            reverses_id: null,
            reason: null,
            receipt_no,
            book_no: src.book_no ?? null,
            device_id: src.device_id,
            device_time: src.device_time,
            collected_by: src.collected_by ?? null
        });

        // `add`, never `put` — see the module header.
        await db.table('ledger').add({ ...wire, business_date: src.business_date, synced: 0 });
        await db.table('outbox').add({ id, queued_at, payload: wire });

        result = { id, receipt_no, queued_at };
    });

    return /** @type {any} */ (result);
}

/**
 * Re-queue an event that is already in the mirror but has no outbox row — the
 * recovery path for a conflict a human resolved as `reinjected`, and for a mirror
 * row whose outbox entry was lost.
 *
 * It re-uses the row's ORIGINAL UUID (harness F14), consumes no receipt number,
 * and is idempotent: an id already in the outbox is left exactly where it is,
 * because moving it would change its drain order.
 *
 * @param {import('dexie').Dexie} db
 * @param {string} id
 * @returns {Promise<{requeued:boolean, reason?:string}>}
 */
export async function requeueEvent(db, id) {
    let outcome = { requeued: false, reason: 'not-in-mirror' };
    await db.transaction('rw', db.table('ledger'), db.table('outbox'), async () => {
        const row = await db.table('ledger').get(id);
        if (!row) return;
        if (await db.table('outbox').get(id)) {
            outcome = { requeued: false, reason: 'already-queued' };
            return;
        }
        const wire = buildWire(row);
        await db.table('ledger').update(id, { synced: 0 });
        await db.table('outbox').add({ id, queued_at: nextQueuedAt(), payload: wire });
        outcome = { requeued: true };
    });
    return outcome;
}

/**
 * Every ledger event this device knows about — its own writes and everything the
 * pull brought back. The input the caller hands to `money/` for any projection.
 *
 * @param {import('dexie').Dexie} db
 * @returns {Promise<object[]>}
 */
export async function getAllEvents(db) {
    return db.table('ledger').toArray();
}

/**
 * This device's events for one business day: the set that folds into a day close.
 *
 * Corrections are INCLUDED even though they were authored elsewhere, whenever they
 * target a sale in the day's set. A `void` is admin-authored and online-only, so it
 * carries a different `device_id` and possibly a different date, but it cancels
 * cash that this drawer is holding — excluding it would show an honest cashier a
 * false surplus.
 *
 * @param {import('dexie').Dexie} db
 * @param {string} deviceId
 * @param {string} businessDate `YYYY-MM-DD` (WAT), from `money/business-date.js`.
 * @returns {Promise<object[]>}
 */
export async function getDeviceDayEvents(db, deviceId, businessDate) {
    const dayEvents = await db.table('ledger')
        .where('[device_id+business_date]')
        .equals([deviceId, businessDate])
        .toArray();

    const dayIds = new Set(dayEvents.map(e => e.id));
    if (dayIds.size === 0) return dayEvents;

    const corrections = await db.table('ledger')
        .filter(e => (e.kind === 'void' || e.kind === 'discount')
            && dayIds.has(e.reverses_id)
            && !dayIds.has(e.id))
        .toArray();

    return dayEvents.concat(corrections);
}

/**
 * Read this device's identity from meta.
 * @param {import('dexie').Dexie} db
 * @returns {Promise<string|null>}
 */
export async function getDeviceId(db) {
    return getMeta(db, META_KEYS.DEVICE_ID, null);
}

/**
 * Record this device's identity (from `register_device`). Written once at setup.
 * @param {import('dexie').Dexie} db
 * @param {string} deviceId
 * @returns {Promise<void>}
 */
export async function setDeviceId(db, deviceId) {
    await setMeta(db, META_KEYS.DEVICE_ID, deviceId);
}

// ---------------------------------------------------------------------------
// validation — refuses, never half-accepts
// ---------------------------------------------------------------------------

/**
 * @param {object} f
 * @param {{code:string, field?:string, message:string}[]} issues
 */
function validateSale(f, issues) {
    if (!f || typeof f !== 'object') {
        issues.push({ code: 'malformed', message: 'fields must be an object' });
        return;
    }

    for (const field of ['device_id', 'device_time', 'business_date']) {
        if (!isPresent(f[field])) {
            issues.push({ code: 'missing', field, message: `${field} is required` });
        }
    }

    if (!CHANNELS.has(f.channel)) {
        issues.push({ code: 'channel', field: 'channel', message: 'channel must be regie or pharmacy' });
    }

    // Invariant 5: positive integer FCFA. XAF has no subunit; no float ever reaches
    // the money path, and `parseFloat` NaN never slips past a `<= 0` guard here.
    if (!isPositiveInt(f.amount)) {
        issues.push({ code: 'amount', field: 'amount', message: 'amount must be a positive integer (FCFA)' });
    }

    if (f.collected_by != null && !isPresent(f.collected_by)) {
        issues.push({ code: 'collected_by', field: 'collected_by', message: 'collected_by, when present, must be an id' });
    }

    validateItems(f, issues);
}

/**
 * Line items must arrive in the SPEC shape — `{item_id, item_kind, qty, unit_price,
 * line_total}` — not the legacy `{name, quantity, unit_price, total, unit}`.
 *
 * This is audit C4 refused at the door. The legacy pharmacy line carries no `id` at
 * all, which is why every offline pharmacy sale called `decrement_drug_stock` with
 * `undefined`, had the error swallowed by a per-item catch, and was still marked
 * `'synced'`. A line the stock fold cannot attribute to a drug is not a line — it is
 * inventory quietly leaving the building.
 *
 * @param {object} f
 * @param {{code:string, field?:string, message:string}[]} issues
 */
function validateItems(f, issues) {
    if (!Array.isArray(f.items) || f.items.length === 0) {
        issues.push({ code: 'missing-items', field: 'items', message: 'a sale must carry at least one line item' });
        return;
    }

    let sum = 0;
    f.items.forEach((line, i) => {
        const at = `items[${i}]`;
        if (!line || typeof line !== 'object') {
            issues.push({ code: 'malformed-line', field: at, message: `${at} must be an object` });
            return;
        }
        if (!isPresent(line.item_id)) {
            issues.push({ code: 'missing-item-id', field: `${at}.item_id`, message: `${at}.item_id is required (audit C4)` });
        }
        if (!ITEM_KINDS.has(line.item_kind)) {
            issues.push({ code: 'unknown-item-kind', field: `${at}.item_kind`, message: `${at}.item_kind must be revenue_type or drug` });
        }
        if (!isPositiveInt(line.qty)) {
            issues.push({ code: 'qty-must-be-integer', field: `${at}.qty`, message: `${at}.qty must be a positive integer` });
        }
        if (!isNonNegativeInt(line.unit_price)) {
            issues.push({ code: 'unit-price', field: `${at}.unit_price`, message: `${at}.unit_price must be a non-negative integer (FCFA)` });
        }
        if (!isNonNegativeInt(line.line_total)) {
            issues.push({ code: 'line-total', field: `${at}.line_total`, message: `${at}.line_total must be a non-negative integer (FCFA)` });
            return;
        }
        // Audit M2: line items and totals must not round independently. In integer
        // FCFA there is nothing to round, so the identity must hold exactly.
        if (isPositiveInt(line.qty) && isNonNegativeInt(line.unit_price)
            && line.line_total !== line.qty * line.unit_price) {
            issues.push({
                code: 'line-total-mismatch',
                field: `${at}.line_total`,
                message: `${at}.line_total must equal qty × unit_price`
            });
        }
        sum += line.line_total;
    });

    // The receipt the patient holds shows the lines AND the total; they must be the
    // same number. A sale whose lines do not add up to its amount is a sale nobody
    // can reconcile later.
    if (isPositiveInt(f.amount) && issues.length === 0 && sum !== f.amount) {
        issues.push({
            code: 'amount-line-mismatch',
            field: 'amount',
            message: `amount (${f.amount}) must equal the sum of line_total (${sum})`
        });
    }
}

/** Positive integer FCFA. @param {*} v @returns {boolean} */
function isPositiveInt(v) {
    return Number.isInteger(v) && v > 0;
}

/** Non-negative integer FCFA (a free line is legitimate; a fractional one is not). */
function isNonNegativeInt(v) {
    return Number.isInteger(v) && v >= 0;
}

/** Non-empty string / non-null presence. @param {*} v @returns {boolean} */
function isPresent(v) {
    return typeof v === 'string' ? v.length > 0 : v != null;
}

/**
 * Project the §6 wire payload, defaulting every absent slot to `null` so the JSON
 * shape is stable field-for-field on every event this device ever sends.
 * @param {object} src
 * @returns {Record<string, *>}
 */
function buildWire(src) {
    /** @type {Record<string, *>} */
    const wire = {};
    for (const k of WIRE_FIELDS) wire[k] = src[k] === undefined ? null : src[k];
    return wire;
}

/** `crypto.randomUUID()` — the sole UUID source (invariant 2). @returns {string} */
function defaultUuid() {
    return crypto.randomUUID();
}
